/**
 * Clock - FNAF-style in-game clock
 * Driven by nightProgress (0-100) — 0% = 12 AM, 100% = 6 AM
 */

import React from 'react';

interface ClockProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center';
  nightNumber?: number;
  nightProgress?: number; // 0-100, drives the time display
}

// FNAF hour labels in order: 12 AM → 1 AM → 2 AM → 3 AM → 4 AM → 5 AM → 6 AM
const HOURS = ['12 AM', '1 AM', '2 AM', '3 AM', '4 AM', '5 AM', '6 AM'];

export const Clock: React.FC<ClockProps> = ({
  position = 'top-center',
  nightNumber = 1,
  nightProgress = 0,
}) => {
  // Map 0-100% progress to 0-6 hours (12 AM through 6 AM)
  const totalHours = 6;
  const hourIndex = Math.min(totalHours, Math.floor((nightProgress / 100) * totalHours));
  const currentLabel = HOURS[hourIndex] ?? '6 AM';
  const isSurvived = nightProgress >= 99.5;

  // Progress within the current hour (for sub-hour glow pulsing)
  const progressInHour = ((nightProgress / 100) * totalHours) - hourIndex;
  const dangerLevel = hourIndex / totalHours; // 0 = safe, 1 = dawn

  // Color shifts from dim red → bright red → orange as night progresses
  const r = 255;
  const g = Math.round(isSurvived ? 255 : dangerLevel * 60);
  const b = isSurvived ? 0 : 0;
  const glowColor = isSurvived ? '#00ff88' : `rgb(${r},${g},${b})`;
  const shadowColor = isSurvived ? '#00ff88' : `rgb(${r},${g},${b})`;

  const positionStyles: Record<string, React.CSSProperties> = {
    'top-left':     { top: '20px', left: '20px' },
    'top-right':    { top: '20px', right: '20px' },
    'bottom-left':  { bottom: '20px', left: '20px' },
    'bottom-right': { bottom: '20px', right: '20px' },
    'top-center':   { top: '20px', left: '50%', transform: 'translateX(-50%)' },
  };

  return (
    <div style={{
      position: 'absolute',
      ...positionStyles[position],
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      fontFamily: '"Courier New", monospace',
      pointerEvents: 'none',
      userSelect: 'none',
    }}>
      {/* Night label */}
      <div style={{
        fontSize: '11px',
        color: '#888',
        fontWeight: 'bold',
        letterSpacing: '3px',
        textTransform: 'uppercase',
      }}>
        Night {nightNumber}
      </div>

      {/* Time display — large FNAF-style readout */}
      <div style={{
        padding: '10px 22px',
        backgroundColor: 'rgba(0,0,0,0.92)',
        border: `2px solid ${glowColor}`,
        borderRadius: '6px',
        fontSize: '46px',
        fontWeight: 'bold',
        letterSpacing: '2px',
        textAlign: 'center',
        color: glowColor,
        textShadow: `0 0 18px ${shadowColor}, 0 0 6px ${shadowColor}`,
        minWidth: '160px',
        lineHeight: 1,
        transition: 'color 0.8s, border-color 0.8s, text-shadow 0.8s',
      }}>
        {currentLabel}
      </div>

      {/* Hour tick bar — 6 segments like FNAF */}
      <div style={{
        display: 'flex',
        gap: '3px',
        marginTop: '3px',
      }}>
        {Array.from({ length: totalHours }).map((_, i) => {
          const filled = i < hourIndex || (i === hourIndex && progressInHour > 0.1);
          const active  = i === hourIndex;
          return (
            <div key={i} style={{
              width: '22px',
              height: '5px',
              borderRadius: '2px',
              backgroundColor: filled
                ? glowColor
                : 'rgba(255,255,255,0.12)',
              boxShadow: active ? `0 0 6px ${glowColor}` : 'none',
              transition: 'background-color 0.4s',
            }} />
          );
        })}
      </div>

      {/* 6 AM survived flash */}
      {isSurvived && (
        <div style={{
          fontSize: '12px',
          color: '#00ff88',
          fontWeight: 'bold',
          marginTop: '4px',
          animation: 'clockBlink 0.5s infinite',
        }}>
          YOU SURVIVED!
        </div>
      )}

      <style>{`
        @keyframes clockBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};
